<template>
    <div class="d-flex flex-column flex-grow-1 product-catalog">
        <v-toolbar
            color="secondary"
            density="compact"
            rounded="t b-0"
        >
            <v-toolbar-title>Product Catalog</v-toolbar-title>

            <v-spacer></v-spacer>

            <!-- Reload -->
            <v-btn
                @click.prevent="onReload()"
                :variant="isMobile ? 'plain' : 'text'"
                :icon="isMobile"
            >
                <v-icon :icon="mdiCached"></v-icon>
                <span v-if="!isMobile" class="ml-1">Reload</span>
            </v-btn>

            <!-- Menus -->
            <v-tooltip
                text="Products Menu"
                open-delay="800"
            >
                <template #activator="{ props }">
                    <v-btn
                        v-bind="props"
                        :icon="mdiViewListOutline"
                        aria-label="Show Products Menu"
                        @click.prevent="onShowMenu()"
                    ></v-btn>
                </template>
            </v-tooltip>
        </v-toolbar>

        <!-- Main Section -->
        <v-layout
            class="fill-height"
        >
            <!-- Links Panel -->
            <v-navigation-drawer
                v-model="menuPanelModel"
                location="right"
                width="320"
                :temporary="!menuPanelPinned && !isMobile"
            >
                <template #prepend>
                    <div
                        class="d-flex align-center justify-start"
                    >
                        <v-btn
                            size="small"
                            variant="plain"
                            color="secondary"
                            :icon="menuPanelPinned ? mdiPin : mdiPinOutline"
                            @click="menuPanelPinned = !menuPanelPinned"
                        ></v-btn>
                        <h2
                            class="text-title-large font-weight-bold text-uppercase py-0 my-0"
                        >
                            Products
                        </h2>
                    </div>
                    <div
                        class="px-4 pt-4"
                    >
                        <v-row>
                            <v-col cols="12">
                                <AssortmentTypeSelectorInput
                                    v-model="filterForm.assortment_type"
                                    title="Assortment Type"
                                    :omit="['Discontinued']"
                                    hide-details
                                ></AssortmentTypeSelectorInput>
                            </v-col>
                            <v-col cols="12">
                                <v-switch
                                    v-model="filterForm.has_upc"
                                    label="Retail packaging only"
                                    color="primary"
                                    density="compact"
                                    true-value="true"
                                    false-value="false"
                                    hide-details
                                    inset
                                ></v-switch>
                            </v-col>
                            <v-col
                                cols="12"
                                class="d-flex flex-column align-end"
                            >
                                <v-btn

                                    color="secondary"
                                    variant="flat"
                                    @click="onFilterReset"
                                >
                                    Reset
                                </v-btn>
                            </v-col>
                        </v-row>

                        <v-divider
                            class="my-4"
                        ></v-divider>

                        <v-text-field
                            v-model="menuSearchTerm"
                            placeholder="Filter Menu"
                            variant="outlined"
                            density="compact"
                            @update:model-value="throttledTermChangeFn"
                            @click:clear="resetSearch"
                            hide-details
                            clearable
                        ></v-text-field>
                    </div>
                </template>
                <div
                    v-if="menusObjFiltered?.length"
                >
                    <ProductCatalogMenu
                        :items="menusObjFiltered"
                        @on-select="onSelect"
                    ></ProductCatalogMenu>

                    <!-- TODO: Replace ProductCatalogMenu once 'tree' it's bug-free -->
                    <!-- <ProductCatalogTreeMenu
                        :items="menusObjFiltered"
                        :search="menuSearchTerm"
                    ></ProductCatalogTreeMenu> -->
                </div>
            </v-navigation-drawer>
            <v-main tag="div">
                <Dimmer
                    :hide-spinner="false"
                    :model-value="isLoading"
                    fixed-spinner
                >
                    <template #content>
                        <div
                            class="py-4"
                            :class="{ 'pe-4': !isMobile && menuPanelModel }"
                        >
                            <template
                                v-if="!lo_isError(contentObj) && contentObj && contentLoadingStatus !== 'pending'"
                            >
                                <slot
                                    name="default"
                                    :modelValue="contentObj"
                                    :loadingStatus="contentLoadingStatus"
                                />
                                <v-pagination
                                    v-if="isPaginated"
                                    v-model="pagination.page"
                                    :length="pagination.totalPages"
                                    @update:modelValue="onPageChange($event)"
                                    show-first-last-page
                                ></v-pagination>
                            </template>
                            <template
                                v-else-if="!isMounted"
                            >
                                <div>Loading...</div>
                            </template>
                            <template
                                v-else-if="marketsObjStatus !== 'pending' && contentLoadingStatus === 'idle' && !contentObj"
                            >
                                <v-empty-state
                                    title="No Records Found"
                                    text="Try selecting a different item from the menu"
                                    :icon="mdiAlert"
                                ></v-empty-state>
                            </template>
                        </div>
                    </template>
                </Dimmer>

            </v-main>
        </v-layout>
    </div>
</template>

<script lang="ts" setup>
    import ProductCatalogMenu from '~/components/product-catalog/ProductCatalogMenu/ProductCatalogMenu.vue';
    // import ProductCatalogTreeMenu from '~/components/product-catalog/ProductCatalogTreeMenu/ProductCatalogTreeMenu.vue';

    import AssortmentTypeSelectorInput from '~/components/controls/data-filters/AssortmentTypeSelectorInput/AssortmentTypeSelectorInput.vue';
    import { useMounted } from '@vueuse/core';
    import { isError as lo_isError } from 'es-toolkit';
    import { throttle as lo_throttle } from 'es-toolkit';
    import { getSingleParam } from 'x1-base/utils';
    import {
        useIsMobile,
        useProducts,
        useUidUtils,
        useApiCommon,
        useApiQueryParams,
        useProductCatalog
    } from '~/composables';
    import type {
        Product,
        ProductMarket,
        ProductCatalogMenuSelection
    } from '~/types';
    import {
        mdiAlert,
        mdiPin,
        mdiPinOutline,
        mdiViewListOutline,
        mdiCached
    } from '@mdi/js';

    const props = defineProps<{
        currentDeeplink: Nilish<string>;
    }>();

    const emit = defineEmits<{
        'on-path-change': [value: Nullable<ProductCatalogMenuSelection>];
    }>();

    const route = useRoute();
    const isMounted = useMounted();
    const {
        filterForm,
        findMenuSelectionItem,
        filterMenu
    } = useProductCatalog();
    const siteCtxObj = { siteContext: 'product_catalog' };
    const { isMobile } = useIsMobile();
    const useApiCommonObj = useApiCommon({ ... siteCtxObj });
    const { getTypeByUid } = useUidUtils();
    const { getMarkets, getProduct } = useProducts({ ... siteCtxObj });

    const menuPanelModel = ref<boolean>(!isMobile.value);
    const menuPanelPinned = ref<boolean>(!isMobile.value);

    const currentSelectedObj = ref<Nilish<ProductCatalogMenuSelection>>();

    const menuSearchTerm = ref<string>('');
    const menusObjFiltered = ref<ProductMarket[]>([]);

    const throttledTermChangeFn = lo_throttle(onSearch, 1200, { edges: ['trailing'] });
    const isPaginated = ref<boolean>(false);
    const isLoading = computed(() => marketsObjStatus.value === 'pending' || contentLoadingStatus.value === 'pending');
    const filterFormDefaults: Record<string, Nilish<string>> = {
        assortment_type: null,
        has_upc: 'true'
    };
    onFilterReset();

    const {
        pagination,
        updatePagination,
        getApiQueryParams,
        clear: clearParams
    } = useApiQueryParams({
        key: 'ProductCatalogContent',
        resetOnUnload: true,
        opts: {
            page_size: 8
        }
    });

    async function getMarketsData(): Promise<ProductMarket[] | Error> {
        const response = await getMarkets({ params: { all: true } });

        if (lo_isError(response)) {
            return new Error('Could not fetch data');
        }

        return response.data;
    }

    const {
        data: marketsObj,
        refresh: refreshMarketsObj,
        status: marketsObjStatus
    } = useLazyAsyncData<ProductMarket[] | Error>(
        'keyProdCatMarket',
        () => getMarketsData(),
        {
            server: false
        }
    );

    const unWatchMarketsObj = watch(
        marketsObj,
        (newVal, oldVal) => {
            resetSearch();
            selectInitialItem(props.currentDeeplink);
        }
    );

    async function getContent(): Promise<Nilish<Product | Product[] | Error>> {
        const { uid, path } = currentSelectedObj.value ?? {};
        const type = getTypeByUid(uid);

        if (!path || !uid || !type) {
            console.warn('Data missing from selection: ', currentSelectedObj.value);
            return Promise.resolve(null);
        }

        const response = (type === 'product') ?
            await getProduct(uid) :
            await useApiCommonObj.getPagedListItems<Product>('products/', {
                params: Object.assign({
                    [type]: uid
                }, getApiQueryParams(), filterForm.value)
            });

        if (lo_isError(response)) {
            return response
        }

        if ('pagination' in response) {
            isPaginated.value = true;
            updatePagination(response.pagination);
            return response.data;
        }

        isPaginated.value = false;
        return response;
    }

    const {
        data: contentObj,
        refresh: onContentReload,
        status: contentLoadingStatus
    } = useLazyAsyncData<Nilish<Product | Product[] | Error>>(
        'keyProdCatContent',
        () => getContent(),
        {
            immediate: false,
            server: false
        }
    );

    const unWatchFilterForm = watch(
        filterForm,
        (newVal, oldVal) => {
            setQueryParams(newVal);
            onContentReload();
        },
        {
            deep: true
        }
    );

    function onShowMenu(): void {
        menuPanelModel.value = !menuPanelModel.value;
    }

    function onSelect(selected: Nullable<ProductCatalogMenuSelection>): void {
        emit('on-path-change', selected);

        if (!selected) {
            // clear selected
            console.info('CLEAR SELECTED...');
            return;
        }

        clearParams();
        currentSelectedObj.value = selected;
        onContentReload();
    }

    function onSearch(term: Nilish<string>): void {
        if (term && !lo_isError(marketsObj.value) && marketsObj.value?.length) {
            menusObjFiltered.value = filterMenu(marketsObj.value, term);
        }
    }

    function resetSearch(): void {
        menuSearchTerm.value = '';
        menusObjFiltered.value = (!lo_isError(marketsObj.value) && Array.isArray(marketsObj.value)) ? marketsObj.value : [];
    }

    function onReload(): void {
        refreshMarketsObj();
    }

    async function onPageChange(page: number): Promise<void> {
        updatePagination({ page });
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        await nextTick();
        onContentReload();
    }

    function selectInitialItem(deeplink?: Nilish<string>): void {
        if (!lo_isError(marketsObj.value) && marketsObj.value?.length) {
            const selectedObj = (deeplink) ?
                findMenuSelectionItem(marketsObj.value, deeplink) :
                marketsObj.value[0];

            if (selectedObj) {
                nextTick(() => {
                    onSelect(selectedObj);
                });
            }
        }
    }

    function onFilterReset(): void {
        filterForm.value = Object.assign({}, filterFormDefaults);
    }

    function getFilterParamsFromQuery(): Record<string, Nilish<string>> {
        const params: Record<string, Nilish<string>> = {};
        const { query } = route;

        Object.keys(filterFormDefaults).forEach((key) => {
            if (query[key]) {
                params[key] = getSingleParam(query, key);
            }
        });

        return Object.assign({}, filterFormDefaults, params);
    }

    async function setQueryParams(params: Nullable<Record<string, Nilish<string>>>): Promise<void> {
        const query = params ? Object.assign({}, route.query, params) : {};
        await navigateTo({ path: route.path, query, replace: true });
    }

    onBeforeMount(() => {
        filterForm.value = getFilterParamsFromQuery();
    });

    onBeforeUnmount(() => {
        unWatchMarketsObj();
        unWatchFilterForm();
    });
</script>

<style lang="scss">
.product-catalog {

    .v-list-item {
        user-select: none;
    }

    th, td {
        font-weight: bold;
    }

    th:last-child,
    td:last-child {
        text-align: end;
        color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
        font-weight: normal;
    }

    th:first-child,
    td:first-child {
        text-align: start;
    }

    .attr-table {
        &.attr-table-pricing {
            th:first-child,
            td:first-child {
                width: 1px;
            }
        }

        thead {
            th, td {
                text-transform: uppercase;
                color: rgb(var(--v-theme-on-secondary)) !important;
                background-color: rgb(var(--v-theme-secondary));
            }
        }
    }
}
</style>
